import React from 'react';
import { NavLink } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from '../app-header.module.less';

const activeClassName = style.active;
const linkClassName = style['app-header-route'];
const dropdownMenuItemClassName = style['routes-dropdown-item'];

/**
 * @param {object} props
 * @param {React.ReactNode} props.children - The link text/content
 * @param {() => void} [props.onClick] - Click handler for disabled navigation
 * @param {boolean} props.isNavigationDisabled - Whether navigation is disabled
 * @param {(match: object, location: object) => boolean} [props.isActive] - Function to determine if link is active
 * @param {string} [props.to] - The route to navigate to
 * @returns {React.JSX.Element}
 */
const AppHeaderLinkDropdownMenuItem = ({ children, onClick, isNavigationDisabled, isActive, to }) => {
  return isNavigationDisabled ? (
    <DropdownMenuItem className={dropdownMenuItemClassName} onClick={onClick}>
      <span className={linkClassName}>{children}</span>
    </DropdownMenuItem>
  ) : (
    // todo: Ideally we shouldn't have a NavLink inside a DropdownMenuItem, as this results in an <a> inside a <button>, which is invalid HTML.
    //  However, this existed prior to refactoring so I'm going to leave it as is for now.
    <DropdownMenuItem className={dropdownMenuItemClassName}>
      <NavLink activeClassName={activeClassName} className={linkClassName} isActive={isActive} to={to}>
        {children}
      </NavLink>
    </DropdownMenuItem>
  );
};

/**
 * @param {object} props
 * @param {React.ReactNode} props.children - The link text/content
 * @param {() => void} [props.onClick] - Click handler for disabled navigation
 * @param {string} [props.to] - The route to navigate to
 * @param {(match: object, location: object) => boolean} [props.isActive] - Function to determine if link is active
 * @param {boolean} props.isNavigationDisabled - Whether navigation is disabled
 * @returns {React.JSX.Element}
 */
const AppHeaderNavLink = ({ children, onClick, to, isActive, isNavigationDisabled }) => {
  return isNavigationDisabled ? (
    <Button className={linkClassName} onClick={onClick} theme={THEME_CONSTANTS.unstyled}>
      {children}
    </Button>
  ) : (
    <NavLink activeClassName={activeClassName} className={linkClassName} isActive={isActive} to={to}>
      {children}
    </NavLink>
  );
};

/**
 * @param {object} props
 * @param {boolean} props.isCollapsed - Whether the header is collapsed to show dropdown
 * @param {boolean} props.isNavigationDisabled - Whether navigation is disabled
 * @param {React.ReactNode} props.children - The link text/content
 * @param {() => void} [props.onClick] - Click handler for disabled navigation
 * @param {string} [props.to] - The route to navigate to
 * @param {(match: object, location: object) => boolean} [props.isActive] - Function to determine if link is active
 * @returns {React.JSX.Element}
 */
export const AppHeaderLinkContent = ({ isCollapsed, isNavigationDisabled, children, onClick, to, isActive }) => {
  return isCollapsed ? (
    <AppHeaderLinkDropdownMenuItem isActive={isActive} isNavigationDisabled={isNavigationDisabled} onClick={onClick} to={to}>
      {children}
    </AppHeaderLinkDropdownMenuItem>
  ) : (
    <AppHeaderNavLink isActive={isActive} isNavigationDisabled={isNavigationDisabled} onClick={onClick} to={to}>
      {children}
    </AppHeaderNavLink>
  );
};
